/**
 * Task Redux slice
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { taskAPI } from '@/api/taskAPI';
import { Task, TaskWithDetails, CreateTaskData, UpdateTaskData, TaskFilters, Subtask, CreateSubtaskData, UpdateSubtaskData } from '@/types';
import toast from 'react-hot-toast';

interface TaskState {
  tasks: Task[];
  backlogTasks: Task[];
  recentTasks: Task[];
  currentTask: TaskWithDetails | null;
  subtasks: Subtask[];
  loading: boolean;
  error: string | null;
  filters: TaskFilters;
  mutating: boolean;
}

const initialState: TaskState = {
  tasks: [],
  backlogTasks: [],
  recentTasks: [],
  currentTask: null,
  subtasks: [],
  loading: false,
  error: null,
  filters: {},
  mutating: false,
};

// Async thunks
export const fetchTasks = createAsyncThunk(
  'tasks/fetchAll',
  async (filters: TaskFilters = {}, { rejectWithValue }) => {
    try {
      const tasks = await taskAPI.getTasks(filters);
      return tasks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch tasks');
    }
  }
);

export const fetchBacklogTasks = createAsyncThunk(
  'tasks/fetchBacklog',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const tasks = await taskAPI.getBacklogTasks(projectId);
      return tasks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch backlog');
    }
  }
);

export const fetchRecentTasks = createAsyncThunk(
  'tasks/fetchRecent',
  async ({ limit, skip, project_ids }: { limit?: number; skip?: number, project_ids?:string[] } = {}, { rejectWithValue }) => {
    try {
      const tasks = await taskAPI.getRecentTasks(limit, skip, project_ids);
      return tasks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch recent tasks');
    }
  }
);

export const fetchAllSubtasks = createAsyncThunk(
  'tasks/fetchAllSubtasks',
  async (filters: TaskFilters = {}, { rejectWithValue }) => {
    try {
      const subtasks = await taskAPI.getAllSubtasks(filters);
      return subtasks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch subtasks');
    }
  }
);

export const fetchTask = createAsyncThunk(
  'tasks/fetchOne',
  async (id: string, { rejectWithValue }) => {
    try {
      const task = await taskAPI.getTask(id);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch task');
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/create',
  async (data: CreateTaskData, { rejectWithValue }) => {
    try {
      const task = await taskAPI.createTask(data);
      toast.success('Task created successfully!');
      return task;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
      return rejectWithValue(error.response?.data?.detail || 'Failed to create task');
    }
  }
);

export const updateTask = createAsyncThunk(
  'tasks/update',
  async ({ id, data }: { id: string; data: UpdateTaskData }, { rejectWithValue }) => {
    try {
      const task = await taskAPI.updateTask(id, data);
      toast.success('Task updated successfully!');
      return task;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
      return rejectWithValue(error.response?.data?.detail || 'Failed to update task');
    }
  }
);

// Alias for backwards compatibility
export const updateTaskStatus = updateTask;

export const deleteTask = createAsyncThunk(
  'tasks/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await taskAPI.deleteTask(id);
      toast.success('Task deleted successfully!');
      return id;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete task');
    }
  }
);

export const archiveTask = createAsyncThunk(
  'tasks/archive',
  async (id: string, { rejectWithValue }) => {
    try {
      const task = await taskAPI.archiveTask(id);
      toast.success('Task archived successfully!');
      return task;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to archive task');
      return rejectWithValue(error.response?.data?.detail || 'Failed to archive task');
    }
  }
);

export const unarchiveTask = createAsyncThunk(
  'tasks/unarchive',
  async (id: string, { rejectWithValue }) => {
    try {
      const task = await taskAPI.unarchiveTask(id);
      toast.success('Task unarchived successfully!');
      return task;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to unarchive task');
      return rejectWithValue(error.response?.data?.detail || 'Failed to unarchive task');
    }
  }
);

export const fetchSubtasks = createAsyncThunk(
  'tasks/fetchSubtasks',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const subtasks = await taskAPI.getSubtasks(taskId);
      return subtasks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch subtasks');
    }
  }
);

export const createSubtask = createAsyncThunk(
  'tasks/createSubtask',
  async ({ taskId, data }: { taskId: string; data: CreateSubtaskData }, { rejectWithValue }) => {
    try {
      const subtask = await taskAPI.createSubtask(taskId, data);
      toast.success('Subtask created successfully!');
      return subtask;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create subtask');
      return rejectWithValue(error.response?.data?.detail || 'Failed to create subtask');
    }
  }
);

export const updateSubtask = createAsyncThunk(
  'tasks/updateSubtask',
  async ({ subtaskId, data }: { subtaskId: string; data: UpdateSubtaskData }, { rejectWithValue }) => {
    try {
      const subtask = await taskAPI.updateSubtask(subtaskId, data);
      toast.success('Subtask updated successfully!');
      return subtask;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update subtask');
      return rejectWithValue(error.response?.data?.detail || 'Failed to update subtask');
    }
  }
);

export const deleteSubtask = createAsyncThunk(
  'tasks/deleteSubtask',
  async (subtaskId: string, { rejectWithValue }) => {
    try {
      await taskAPI.deleteSubtask(subtaskId);
      toast.success('Subtask deleted successfully!');
      return subtaskId;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete subtask');
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete subtask');
    }
  }
);

export const updateTaskThunk = createAsyncThunk(
  'tasks/updateTaskThunk',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const task = await taskAPI.updateTask(id, data);
      return task;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update task');
    }
  }
);

// Task slice
const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setTasks: (state, action) => {
      state.tasks = action.payload;
      state.loading = false;
      state.error = null;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearCurrentTask: (state) => {
      state.currentTask = null;
    },
    clearSubtasks: (state) => {
      state.subtasks = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    applyTaskUpdate: (state, action) => {
      const updatedTask = action.payload;
      if (!updatedTask || !updatedTask.id) return;

      const applyToList = (list: Task[]) => {
        const idx = list.findIndex((item) => item.id === updatedTask.id);
        if (idx !== -1) {
          list[idx] = { ...list[idx], ...updatedTask };
        }
      };

      applyToList(state.tasks);
      applyToList(state.backlogTasks);
      applyToList(state.recentTasks);

      if (state.currentTask && state.currentTask.id === updatedTask.id) {
        state.currentTask = { ...state.currentTask, ...updatedTask };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = false;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
        state.mutating = false;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Fetch backlog tasks
      .addCase(fetchBacklogTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = false;
      })
      .addCase(fetchBacklogTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.backlogTasks = action.payload;
        state.mutating = false;
      })
      .addCase(fetchBacklogTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Fetch recent tasks
      .addCase(fetchRecentTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = false;
      })
      .addCase(fetchRecentTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.recentTasks = action.payload;
        state.mutating = false;
      })
      .addCase(fetchRecentTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Fetch single task
      .addCase(fetchTask.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = false;
      })
      .addCase(fetchTask.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTask = action.payload;
        state.mutating = false;
      })
      .addCase(fetchTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Create task
      .addCase(createTask.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = true;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks.unshift(action.payload);
        state.mutating = false;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Update task
      .addCase(updateTask.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = true;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex(task => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        if (state.currentTask && state.currentTask.id === action.payload.id) {
          state.currentTask = { ...state.currentTask, ...action.payload };
        }
        state.mutating = false;
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Delete task
      .addCase(deleteTask.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = true;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = state.tasks.filter(task => task.id !== action.payload);
        state.backlogTasks = state.backlogTasks.filter(task => task.id !== action.payload);
        if (state.currentTask && state.currentTask.id === action.payload) {
          state.currentTask = null;
        }
        state.mutating = false;
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Archive task
      .addCase(archiveTask.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = true;
      })
      .addCase(archiveTask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex(task => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        state.mutating = false;
      })
      .addCase(archiveTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Unarchive task
      .addCase(unarchiveTask.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = true;
      })
      .addCase(unarchiveTask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex(task => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        state.mutating = false;
      })
      .addCase(unarchiveTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Fetch subtasks
      .addCase(fetchSubtasks.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = false;
      })
      .addCase(fetchSubtasks.fulfilled, (state, action) => {
        state.loading = false;
        state.subtasks = action.payload;
        state.mutating = false;
      })
      .addCase(fetchSubtasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Fetch all subtasks
      .addCase(fetchAllSubtasks.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = false;
      })
      .addCase(fetchAllSubtasks.fulfilled, (state, action) => {
        state.loading = false;
        state.subtasks = action.payload;
        state.mutating = false;
      })
      .addCase(fetchAllSubtasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Create subtask
      .addCase(createSubtask.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = true;
      })
      .addCase(createSubtask.fulfilled, (state, action) => {
        state.loading = false;
        state.subtasks.push(action.payload);
        state.mutating = false;
      })
      .addCase(createSubtask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Update subtask
      .addCase(updateSubtask.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = true;
      })
      .addCase(updateSubtask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.subtasks.findIndex(subtask => subtask.id === action.payload.id);
        if (index !== -1) {
          state.subtasks[index] = action.payload;
        }
        state.mutating = false;
      })
      .addCase(updateSubtask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Delete subtask
      .addCase(deleteSubtask.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.mutating = true;
      })
      .addCase(deleteSubtask.fulfilled, (state, action) => {
        state.loading = false;
        state.subtasks = state.subtasks.filter(subtask => subtask.id !== action.payload);
        state.mutating = false;
      })
      .addCase(deleteSubtask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.mutating = false;
      })
      // Update task thunk
      .addCase(updateTaskThunk.fulfilled, (state, action) => {
        const index = state.tasks.findIndex(task => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        if (state.currentTask && state.currentTask.id === action.payload.id) {
          state.currentTask = { ...state.currentTask, ...action.payload };
        }
      })
      .addCase(updateTaskThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, setTasks, clearFilters, clearCurrentTask, clearSubtasks, clearError, applyTaskUpdate } = taskSlice.actions;
export default taskSlice.reducer;